import { IOperationResultNoValue } from '@ViewModels';
import { IModalProps } from '@WebComponents/Modal';
import { TextInputModal } from '@WebComponents/TextInputModal';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEventLogging } from '../../../../models/Logging';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import {
	IndustryAutomationTemplateViewModel,
	IndustryAutomationTemplatesViewModel,
} from '../../../../viewmodels/AdminViewModels';
import { Button } from '../../../../web/components/Button';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';

interface IProps {
	industryAutomationTemplate?: IndustryAutomationTemplateViewModel;
	isOpen: boolean;
	onRequestClose(
		createdAutomationTemplate?: IndustryAutomationTemplateViewModel,
		cancel?: boolean,
		error?: IOperationResultNoValue
	): void;
	templates: IndustryAutomationTemplatesViewModel;
}

export const DuplicateSystemAutomationTemplateModal: React.FC<IProps> = observer(props => {
	const errorMessages = useErrorMessages();
	const { logEvent, logApiError } = useEventLogging('DuplicateAutomationTemplateModal');

	const { templates, industryAutomationTemplate, onRequestClose, isOpen } = props;

	const onModalRequestClose = React.useCallback(
		async (name?: string, cancel?: boolean) => {
			const trimmedName = !cancel ? (name || '').trim() : null;

			let createdTemplate: IndustryAutomationTemplateViewModel = null;

			let err: IOperationResultNoValue = null;
			if (trimmedName) {
				logEvent('DuplicateAutomation', {
					id: industryAutomationTemplate.id,

					nameLength: name.length,
				});
				try {
					createdTemplate = await templates.duplicateTemplate(industryAutomationTemplate, name);
				} catch (error) {
					logApiError('DuplicateAutomationTemplate-Error', error);

					errorMessages.pushApiError(error);

					err = error;
				}
			}
			onRequestClose?.(createdTemplate, cancel, err);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[templates, industryAutomationTemplate, onRequestClose]
	);

	const [modalProps, setModalProps] = React.useState<IModalProps<string>>({
		isOpen,
		onRequestClose: onModalRequestClose,
	});
	React.useEffect(() => {
		if (isOpen !== modalProps.isOpen || onModalRequestClose !== modalProps.onRequestClose) {
			setModalProps({
				...modalProps,
				isOpen,
				onRequestClose: onModalRequestClose,
			});
		}
	}, [isOpen, modalProps, onModalRequestClose]);

	const onRenderCta = React.useCallback(
		(buttonProps?: { onClick: (e: React.MouseEvent<HTMLElement>) => void }) => {
			return <Button onClick={buttonProps?.onClick} label='Duplicate' disabled={templates?.isBusy} />;
		},
		[templates?.isBusy]
	);
	return (
		<TextInputModal
			cta={onRenderCta}
			modalProps={modalProps}
			placeholderText='New Automation name'
			title={`Duplicate Automation: "${industryAutomationTemplate?.name || ''}"`}
		>
			{!!templates?.isBusy && <LoadingSpinner type='small' />}
		</TextInputModal>
	);
});
